// All condition-specific content in one file.
// Edit this file to update copy, techniques, equipment, FAQs.

export const CONDITIONS_DATA = {
  backPain: {
    slug: 'back-pain',
    tag: 'Back & Neck Pain',
    headline: 'Relief from back and neck pain, at home.',
    subheadline: 'DHA-licensed physiotherapists come to you — no commute, no waiting room, no disruption to your day.',
    photo: '/hero-back.jpg',
    metaTitle: 'Home Physiotherapy for Back & Neck Pain in Dubai | MotionCare',

    painPoints: [
      'Sitting at your desk has become genuinely painful',
      'You wake up stiff and it takes an hour to feel normal',
      'The pain travels down your arm or leg',
      'You have tried rest but the pain keeps returning',
      'You cannot find a comfortable position for long',
    ],

    intro: 'Back and neck pain is one of the most common conditions we treat — and one of the most effectively treated at home. Your therapist assesses your posture, movement patterns, and home environment, then builds a treatment plan specifically for you.',

    techniques: [
      { name: 'Manual Therapy & Joint Mobilisation', desc: 'Hands-on techniques to restore movement and reduce stiffness in the spine and surrounding joints.' },
      { name: 'Dry Needling', desc: 'Fine needles target trigger points in tight muscles, releasing tension and reducing referred pain patterns.' },
      { name: 'Postural Correction', desc: 'Assessment of how you sit, stand, and move — with practical corrections that make an immediate difference.' },
      { name: 'Core Strengthening', desc: 'Targeted exercises to build the deep stabilising muscles that support your spine long-term.' },
      { name: 'TENS Therapy', desc: 'Gentle electrical stimulation to block pain signals and promote muscle relaxation.' },
      { name: 'Ergonomic Advice', desc: 'Practical guidance on your desk, chair, and sleeping position to prevent recurrence.' },
    ],

    equipment: [
      'Portable treatment table',
      'TENS / electrical stimulation unit',
      'Resistance bands (multiple resistance levels)',
      'Kinesio tape',
      'Dry needling kit',
      'Postural assessment tools',
    ],

    timeline: [
      { session: 'Session 1', label: 'Assessment & immediate relief', desc: 'Full assessment, diagnosis, first treatment. Most patients feel measurable improvement after session one.' },
      { session: 'Sessions 2–4', label: 'Reduce pain & restore movement', desc: 'Focused treatment. Mobility returns. Pain reduces significantly for most patients.' },
      { session: 'Sessions 5–8', label: 'Strengthen & stabilise', desc: 'Core strengthening begins. You learn exercises to maintain progress between sessions.' },
      { session: 'Sessions 9+', label: 'Long-term prevention', desc: 'Maintenance programme. Reduce risk of recurrence. Return to full daily activity.' },
    ],

    faqs: [
      { q: 'How many sessions will I need?', a: 'Most patients with acute back pain see significant improvement within 4–6 sessions. Chronic conditions typically require 8–12 sessions. Your therapist will give you a clear plan after the first assessment.' },
      { q: 'Is home physiotherapy as effective as a clinic?', a: 'For back and neck pain, home treatment is often more effective. Your therapist sees your actual desk, chair, and sleeping setup — and can make real adjustments to your environment.' },
      { q: 'What if I am in severe pain right now?', a: 'Our therapists are trained to work with patients in acute pain. If your pain is a 9 or 10 out of 10, please contact us first so we can assess whether immediate physiotherapy or a medical review is more appropriate.' },
      { q: 'Do I need a referral from a doctor?', a: 'No referral needed. You can book directly. If you have recent scans or medical reports, please have them available for your therapist.' },
      { q: 'What should I do to prepare for the first visit?', a: 'Clear a space of roughly 2 metres by 2 metres. Wear comfortable, loose clothing. Have any recent medical reports, scans, or X-rays ready. That is all.' },
    ],
  },

  sportsInjury: {
    slug: 'sports-injury',
    tag: 'Sports Injury Recovery',
    headline: 'Get back to full strength, faster.',
    subheadline: 'Expert sports rehabilitation delivered to your door. Whether gym, football, running, or tennis — we build a plan to get you back.',
    photo: '/hero-sports.jpg',
    metaTitle: 'Home Sports Injury Physiotherapy in Dubai | MotionCare',

    painPoints: [
      'You have been told to rest but you are not getting better',
      'The injury is stopping you from training or competing',
      'You are worried about rushing back and making it worse',
      'Clinic appointments do not fit your schedule',
      'You want expert guidance, not generic exercises from YouTube',
    ],

    intro: 'Sports injuries respond exceptionally well to targeted rehabilitation at home. Your therapist brings all necessary equipment, designs a progressive programme specific to your sport and injury, and tracks your recovery session by session.',

    techniques: [
      { name: 'Sport-Specific Rehabilitation', desc: 'Exercise programmes built around your sport — whether football, CrossFit, running, tennis, or swimming.' },
      { name: 'Progressive Loading', desc: 'Systematic strength rebuilding from initial recovery through to return-to-sport readiness.' },
      { name: 'Dry Needling', desc: 'Targets muscle trigger points and scar tissue, accelerating recovery from muscle injuries and strains.' },
      { name: 'Shockwave Therapy', desc: 'Evidence-based treatment for chronic tendon conditions including Achilles tendinopathy and tennis elbow.' },
      { name: 'Manual Therapy', desc: 'Joint and soft-tissue mobilisation to restore full range of motion after injury.' },
      { name: 'Return-to-Sport Planning', desc: 'A structured 4-stage protocol so you return safely — not too early, not too late.' },
    ],

    equipment: [
      'Resistance bands (full range)',
      'Portable soft-tissue massage tools',
      'Shockwave therapy device',
      'Balance and stability boards',
      'Kinesio tape',
      'Exercise prescription sheets (your home programme)',
    ],

    timeline: [
      { session: 'Stage 1', label: 'Pain control & protection', desc: 'Reduce swelling and pain. Protect the injury. Gentle range-of-motion work begins.' },
      { session: 'Stage 2', label: 'Restore movement', desc: 'Full range of motion returns. Gentle strengthening starts. Most daily activities resume.' },
      { session: 'Stage 3', label: 'Rebuild strength', desc: 'Progressive loading. Strength returns to pre-injury levels. Sport-specific movements introduced.' },
      { session: 'Stage 4', label: 'Return to sport', desc: 'High-load, sport-specific training. Performance restored. Prevention plan in place.' },
    ],

    faqs: [
      { q: 'How soon after injury should I start physiotherapy?', a: 'As soon as possible — within 48–72 hours for most soft tissue injuries. Early intervention significantly reduces total recovery time. If you have suspected fractures, dislocations, or severe swelling, seek emergency care first.' },
      { q: 'Do you treat gym injuries as well as sports?', a: 'Yes. We treat all musculoskeletal injuries — gym, CrossFit, team sports, running, racquet sports, and general activity-related injuries.' },
      { q: 'Can you help with recurring injuries?', a: 'Recurring injuries usually have an underlying cause — a muscle imbalance, a movement pattern, or inadequate rehabilitation the first time. We identify and address the root cause, not just the symptom.' },
      { q: 'Will you tell me when I can return to sport?', a: 'Yes. We use objective functional tests to determine when your body is genuinely ready to return — not just when the pain has stopped. Returning too early is one of the main causes of re-injury.' },
      { q: 'Do I need a scan before my first appointment?', a: 'Not necessarily. Our therapists can assess most injuries clinically. If imaging is needed, we will advise you which type and where to get it in Dubai.' },
    ],
  },

  postSurgery: {
    slug: 'post-surgery',
    tag: 'Post-Surgery Rehabilitation',
    headline: 'Recover safely, without leaving home.',
    subheadline: 'Professional rehabilitation after knee, hip, spinal, or shoulder surgery — at your own pace, in your own space.',
    photo: '/hero-surgery.jpg',
    metaTitle: 'Home Post-Surgery Physiotherapy in Dubai | MotionCare',

    painPoints: [
      'Travelling to a clinic is painful and risks your recovery',
      'You are not sure you are doing your exercises correctly',
      'Progress feels slow and you do not know if that is normal',
      'You want professional guidance, not just a sheet of exercises',
      'Family cannot always take you to clinic appointments',
    ],

    intro: 'After surgery, physiotherapy must start quickly — but travelling to a clinic is often painful, inconvenient, and may compromise your recovery. Home physiotherapy allows your therapist to see your real environment, work at your pace, and guide your family or carer as part of the process.',

    techniques: [
      { name: 'Passive & Active Range-of-Motion', desc: 'Gentle, guided movement to restore joint mobility without stressing the surgical site.' },
      { name: 'Muscle Re-activation', desc: 'Targeted techniques to re-engage muscles that have weakened due to surgery, anaesthesia, or bed rest.' },
      { name: 'Scar Tissue Management', desc: 'Gentle massage and mobilisation to prevent adhesion formation and reduce surgical scar tightness.' },
      { name: 'Gait Re-training', desc: 'Re-learning correct walking patterns, especially after knee or hip surgery, to avoid compensation habits.' },
      { name: 'Oedema Management', desc: 'Techniques to reduce post-surgical swelling and promote healthy circulation.' },
      { name: 'Balance & Coordination Restoration', desc: 'Progressive exercises to restore proprioception and stability following lower limb surgery.' },
    ],

    equipment: [
      'Portable treatment table',
      'Resistance bands (light to medium)',
      'Kinesio tape for swelling management',
      'Balance board (for lower limb surgery)',
      'Portable ultrasound therapy device',
      'Grip strengtheners (for upper limb surgery)',
    ],

    timeline: [
      { session: 'Week 1–2', label: 'Protection & pain management', desc: 'Gentle movement, swelling reduction, positioning guidance. Prevent stiffness from setting in.' },
      { session: 'Week 3–6', label: 'Restore range of motion', desc: 'Progressive movement. Strength work begins. Most patients walk independently by end of this phase.' },
      { session: 'Week 7–12', label: 'Rebuild strength & function', desc: 'Full strengthening programme. Return to daily activities. Stair climbing, light walking, driving assessed.' },
      { session: 'Month 4+', label: 'Return to full activity', desc: 'Advanced functional training. Return to work, leisure, and physical activity as appropriate.' },
    ],

    faqs: [
      { q: 'When can I start physiotherapy after surgery?', a: 'This depends on your surgery and your surgeon\'s instructions. In many cases, physiotherapy starts within 24–48 hours of discharge. We coordinate with your surgical team and will not start before you have clearance.' },
      { q: 'Do you need clearance from my surgeon?', a: 'Yes — for post-surgical cases, we require written or verbal clearance from your surgeon before the first session. If you had surgery in the last 14 days, please have your discharge summary and any post-op instructions ready.' },
      { q: 'Can family members be present?', a: 'Absolutely. We encourage it. Your therapist will teach family members or carers safe exercise assistance techniques so your rehabilitation continues between sessions.' },
      { q: 'What surgeries do you cover?', a: 'Knee replacement, hip replacement, ACL reconstruction, shoulder surgery, spinal surgery, rotator cuff repair, ankle surgery, and most other orthopaedic procedures. Please call us to confirm if you are unsure.' },
      { q: 'What if I experience unexpected pain during recovery?', a: 'Our therapists are trained to identify signs that require medical review. If anything concerns them, they will advise you to contact your surgeon and will document the concern. Your safety is always the priority.' },
    ],
  },

  elderlyCare: {
    slug: 'elderly-care',
    tag: 'Elderly Care & Mobility',
    headline: 'Gentle, specialist care for the people you love.',
    subheadline: 'Improving mobility, reducing pain, and supporting independence — in the comfort and safety of home.',
    photo: '/hero-elderly.jpg',
    metaTitle: 'Home Physiotherapy for Elderly Patients in Dubai | MotionCare',

    painPoints: [
      'Getting to a clinic has become difficult or exhausting',
      'You are worried about falls or a decline in mobility',
      'Everyday tasks — stairs, getting up, walking — are becoming harder',
      'You want a specialist, not a general carer',
      'You are booking on behalf of an elderly parent and need someone you can trust',
    ],

    intro: 'For elderly patients, the home environment is not just a convenience — it is clinically significant. Our therapists assess the actual space where your loved one lives, identifies real fall risks, advises on mobility aids, and involves family members directly in the recovery process.',

    techniques: [
      { name: 'Gentle Joint Mobilisation', desc: 'Careful, targeted movement to improve joint range and reduce stiffness — appropriate for arthritis and age-related joint changes.' },
      { name: 'Balance & Fall Prevention', desc: 'Evidence-based balance training to significantly reduce fall risk — one of the most important outcomes of elderly physiotherapy.' },
      { name: 'Strength & Functional Training', desc: 'Exercises focused on the movements that matter most: standing up, walking, climbing stairs, and daily independence.' },
      { name: 'Pain Management', desc: 'Non-invasive techniques including heat therapy, gentle massage, and TENS to manage chronic pain without increasing medication.' },
      { name: 'Stroke Rehabilitation', desc: 'Specialised exercises to restore movement and function following stroke, including upper and lower limb re-training.' },
      { name: 'Carer Education', desc: 'We teach family members and carers safe handling, transfer techniques, and exercises to continue between sessions.' },
    ],

    equipment: [
      'Portable treatment table',
      'Resistance bands (light resistance)',
      'Balance board and stability equipment',
      'TENS therapy unit for pain management',
      'Therapy balls and grip strengtheners',
      'Mobility aid assessment tools',
    ],

    timeline: [
      { session: 'Sessions 1–2', label: 'Assessment & environment review', desc: 'Full assessment of mobility, strength, balance, and the home environment. Fall risks identified and addressed.' },
      { session: 'Sessions 3–6', label: 'Reduce pain & improve movement', desc: 'Pain reduces. Daily movements become easier. Balance training underway.' },
      { session: 'Sessions 7–10', label: 'Build strength & confidence', desc: 'Strength and functional capacity improve. Family trained in home exercise programme.' },
      { session: 'Ongoing', label: 'Maintain independence', desc: 'Maintenance programme. Regular sessions to sustain mobility and prevent decline.' },
    ],

    faqs: [
      { q: 'Is physiotherapy safe for elderly patients at home?', a: 'Yes, and it is often safer than a clinic. Our therapists work at the patient\'s pace, in a familiar environment, with family present if preferred. Every session is adapted to the patient\'s condition that day.' },
      { q: 'Does a family member need to be present?', a: 'For patients over 75 or those with cognitive impairment, we strongly recommend a family member or carer be present. For independent elderly patients, it is encouraged but not required.' },
      { q: 'Can you help with Parkinson\'s disease, arthritis, or stroke recovery?', a: 'Yes. Our therapists have experience with neurological conditions including Parkinson\'s and post-stroke rehabilitation, as well as musculoskeletal conditions including osteoarthritis and osteoporosis.' },
      { q: 'How often should elderly patients have sessions?', a: 'This depends on the patient\'s condition and goals. Typically 2–3 sessions per week during active rehabilitation, reducing to 1 session per week for maintenance. Your therapist will recommend a frequency after the first assessment.' },
      { q: 'Do you offer female therapists for elderly female patients?', a: 'Yes, always. When you request a visit, simply indicate your preference for a female therapist and we will ensure one is assigned.' },
    ],
  },
};